import {
  APPROACH_LEAD_MS,
  FINALE_BAND_START_MS,
  PATTERN_SPACING_MS,
  RUN_DURATION_MS,
  SPAWN_CUTOFF_MS,
  bandForElapsed,
  smoothedApproachLeadMs,
  type Lane,
  type RunBand,
} from "../constants";
import {
  ENCOUNTER_PATTERNS,
  patternsForBand,
  type EncounterKind,
  type EncounterPattern,
} from "../data/encounterPatterns";
import { isFoodKind } from "../data/encounterPatterns";

export interface ScheduledEncounter {
  id: string;
  kind: EncounterKind;
  lane: Lane;
  reachAtMs: number;
  leadMs: number;
  wolfFrom?: Lane;
  wolfTo?: Lane;
  consumed: boolean;
}

export class EncounterDirector {
  private nextPatternAt = 900;
  private lastPatternId = "";
  private spawnIndex = 0;
  private stopped = false;
  private finaleQueued = false;
  readonly scheduled: ScheduledEncounter[] = [];

  stopSpawning(): void {
    this.stopped = true;
  }

  update(elapsedMs: number): ScheduledEncounter[] {
    const spawned: ScheduledEncounter[] = [];

    if (!this.finaleQueued && elapsedMs >= FINALE_BAND_START_MS) {
      spawned.push(...this.queueFinaleTreats(elapsedMs));
      this.finaleQueued = true;
    }

    if (this.stopped) {
      return spawned;
    }

    if (elapsedMs >= SPAWN_CUTOFF_MS) {
      this.stopped = true;
      return spawned;
    }

    if (elapsedMs >= this.nextPatternAt && this.nextPatternAt < SPAWN_CUTOFF_MS) {
      const band = bandForElapsed(this.nextPatternAt);
      const pattern = this.pickPattern(band);
      spawned.push(...this.schedulePattern(pattern, this.nextPatternAt));
      this.nextPatternAt += PATTERN_SPACING_MS[band];
    }

    return spawned;
  }

  private queueFinaleTreats(elapsedMs: number): ScheduledEncounter[] {
    const pattern = ENCOUNTER_PATTERNS.find((item) => item.id === "finale-honey-line");
    if (!pattern) {
      return [];
    }
    const lead = APPROACH_LEAD_MS.finale;
    const startMs = Math.min(elapsedMs + 400, RUN_DURATION_MS - lead - 900);
    return this.schedulePattern(pattern, Math.max(0, startMs));
  }

  private pickPattern(band: RunBand): EncounterPattern {
    const pool = patternsForBand(band).filter((pattern) => pattern.id !== this.lastPatternId);
    const source = pool.length > 0 ? pool : patternsForBand(band);
    const picked = source[Math.floor(Math.random() * source.length)] ?? ENCOUNTER_PATTERNS[0]!;
    this.lastPatternId = picked.id;
    return picked;
  }

  private schedulePattern(pattern: EncounterPattern, startMs: number): ScheduledEncounter[] {
    const leadMs = smoothedApproachLeadMs(startMs);
    const created: ScheduledEncounter[] = [];
    for (const item of pattern.items) {
      const encounter: ScheduledEncounter = {
        id: `${pattern.id}-${this.spawnIndex++}`,
        kind: item.kind,
        lane: item.lane,
        reachAtMs: startMs + item.delayMs + leadMs,
        leadMs,
        wolfFrom: item.wolfFrom,
        wolfTo: item.wolfTo,
        consumed: false,
      };
      this.scheduled.push(encounter);
      created.push(encounter);
    }
    return created;
  }
}

export function textureForKind(kind: EncounterKind): string {
  if (isFoodKind(kind)) {
    return `food-${kind}`;
  }
  switch (kind) {
    case "wolf":
      return "wolf-run-toward";
    case "rock-small":
      return "hazard-rock-small";
    case "rock-large":
      return "hazard-rock-large";
    case "log":
      return "hazard-log";
    case "stump":
      return "hazard-stump";
    case "branch":
      return "hazard-low-branch";
    case "ravine":
      return "hazard-ravine-mid";
    case "thorns":
      return "hazard-thorn-bush";
  }
}

export function displayScaleForKind(kind: EncounterKind): number {
  if (isFoodKind(kind)) {
    return 0.72;
  }
  if (kind === "wolf") {
    return 0.95;
  }
  if (kind === "branch") {
    return 1.75;
  }
  if (kind === "log") {
    return 0.85;
  }
  if (kind === "ravine") {
    return 0.38;
  }
  if (kind === "rock-large") {
    return 0.4;
  }
  if (kind === "thorns") {
    return 1;
  }
  return 0.55;
}

export function laneFitScale(
  kind: EncounterKind,
  pose: { scale: number; roadHalfWidth: number },
  spriteWidth: number,
  laneSpread = 0.56,
): number {
  const desired = pose.scale * displayScaleForKind(kind);
  if (spriteWidth <= 0) {
    return desired;
  }
  const laneWidth = pose.roadHalfWidth * laneSpread;
  const maxW =
    laneWidth * (kind === "branch" ? 1.85 : kind === "log" ? 1.2 : 0.9);
  const width = spriteWidth * desired;
  if (width <= maxW) {
    return desired;
  }
  return desired * (maxW / width);
}

/** Approach depth where a crossing wolf starts sliding (`distance / maxDistance`). */
export const WOLF_CROSS_FAR_DEPTH = 0.72;
/** Approach depth where a crossing wolf finishes in `wolfTo`. */
export const WOLF_CROSS_NEAR_DEPTH = 0.16;

export function destinationLane(encounter: ScheduledEncounter): number {
  if (encounter.kind === "wolf") {
    return encounter.wolfTo ?? encounter.lane;
  }
  return encounter.lane;
}

/** Visual lane only. Collision still uses `destinationLane`. */
export function displayLane(encounter: ScheduledEncounter, elapsedMs: number): number {
  if (encounter.kind !== "wolf") {
    return encounter.lane;
  }
  const from = encounter.wolfFrom ?? encounter.lane;
  const to = encounter.wolfTo ?? encounter.lane;
  if (from === to) {
    return to;
  }
  const depth = (encounter.reachAtMs - elapsedMs) / Math.max(1, encounter.leadMs);
  if (depth >= WOLF_CROSS_FAR_DEPTH) {
    return from;
  }
  if (depth <= WOLF_CROSS_NEAR_DEPTH) {
    return to;
  }
  const t = (WOLF_CROSS_FAR_DEPTH - depth) / (WOLF_CROSS_FAR_DEPTH - WOLF_CROSS_NEAR_DEPTH);
  const eased = t * t * (3 - 2 * t);
  return from + (to - from) * eased;
}
